import React, { useState, useEffect, useContext } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, ClockIcon, ChartBarIcon, CogIcon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownTrayIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface IntegrationsContextType {
  integrations: Integration[];
}

const IntegrationsContext = React.createContext<IntegrationsContextType>({ integrations: [] });

interface IntegrationConfig {
  version?: string;
  rateLimit?: number;
  timeout?: number;
  retryAttempts?: number;
  apiKey?: string;
  apiSecret?: string;
  [key: string]: any;
}

interface IntegrationHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  lastChecked: Date;
  responseTime: number;
  errorRate: number;
  uptime: number;
}

interface IntegrationMetrics {
  totalRequests: number;
  successRate: number;
  averageResponseTime: number;
  successfulRequests: number;
  failedRequests: number;
  lastUpdated: Date;
}

interface Integration {
  id: string;
  name: string;
  provider: string;
  status: 'active' | 'inactive' | 'pending';
  config: IntegrationConfig;
  health: IntegrationHealth;
  metrics: IntegrationMetrics;
  errorLogs: Array<{
    timestamp: Date;
    error: string;
    severity: 'low' | 'medium' | 'high';
    context: string;
  }>;
  apiKey?: string;
  apiSecret?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface FeatureCardProps {
  title: string;
  type: 'health' | 'metrics' | 'error' | 'config' | 'security' | 'status';
  data: any;
  isExpanded: boolean;
  onToggle: () => void;
}

interface FilterOptions {
  startDate: string;
  endDate: string;
  integrationName: string;
  timeRange: '24h' | '7d' | '30d' | 'custom';
}

interface ChartFilterProps {
  onFilterChange: (filters: FilterOptions) => void;
  onExport: (format: 'pdf' | 'csv') => void;
  integrations: Integration[];
}

const ChartFilter: React.FC<ChartFilterProps> = ({ onFilterChange, onExport, integrations }) => {
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: '',
    endDate: '',
    integrationName: '',
    timeRange: '24h'
  });

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    onFilterChange(filters);
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700">Filter Data</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => onExport('pdf')}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            Export PDF
          </button>
          <button
            onClick={() => onExport('csv')}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            Export CSV
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Time Range</label>
          <select
            name="timeRange"
            value={filters.timeRange}
            onChange={handleFilterChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        {filters.timeRange === 'custom' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700">Integration</label>
          <select
            name="integrationName"
            value={filters.integrationName}
            onChange={handleFilterChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">All Integrations</option>
            {integrations.map(integration => (
              <option key={integration.id} value={integration.name}>
                {integration.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={handleApplyFilter}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <FunnelIcon className="h-4 w-4 mr-2" />
          Apply Filters
        </button>
      </div>
    </div>
  );
};

const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  type,
  data,
  isExpanded,
  onToggle,
}) => {
  const [filteredData, setFilteredData] = useState(data);
  const { integrations } = useContext(IntegrationsContext);

  // Utility functions for data filtering and export
  const applyFilters = (data: any, filters: FilterOptions) => {
    const filtered = { ...data };
    
    if (filters.integrationName) {
      Object.keys(filtered).forEach(key => {
        if (Array.isArray(filtered[key])) {
          filtered[key] = filtered[key].filter((item: any) => 
            item.integrationName === filters.integrationName
          );
        }
      });
    }

    if (filters.timeRange !== 'custom') {
      const now = new Date();
      let startDate = new Date();
      
      switch (filters.timeRange) {
        case '24h':
          startDate.setHours(now.getHours() - 24);
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
      }

      Object.keys(filtered).forEach(key => {
        if (Array.isArray(filtered[key])) {
          filtered[key] = filtered[key].filter((item: any) => 
            new Date(item.timestamp) >= startDate && new Date(item.timestamp) <= now
          );
        }
      });
    } else if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      
      Object.keys(filtered).forEach(key => {
        if (Array.isArray(filtered[key])) {
          filtered[key] = filtered[key].filter((item: any) => 
            new Date(item.timestamp) >= startDate && new Date(item.timestamp) <= endDate
          );
        }
      });
    }

    return filtered;
  };

  const convertDataToTableFormat = (data: any) => {
    const tableData: any[] = [];
    
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item: any) => {
          tableData.push({
            type: key,
            ...item
          });
        });
      } else if (typeof value === 'object') {
        tableData.push({
          type: key,
          ...value
        });
      }
    });
    
    return tableData;
  };

  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(header => row[header]));
    
    return [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
  };

  const exportToPDF = (title: string, data: any) => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 25);
    
    const tableData = convertDataToTableFormat(data);
    if (tableData.length > 0) {
      autoTable(doc, {
        head: [Object.keys(tableData[0])],
        body: tableData.map(row => Object.values(row)),
        startY: 35,
      });
    }
    
    doc.save(`${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.pdf`);
  };

  const exportToCSV = (title: string, data: any) => {
    const tableData = convertDataToTableFormat(data);
    const csvContent = convertToCSV(tableData);
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.csv`;
    link.click();
  };

  const handleFilterChange = (filters: FilterOptions) => {
    const filtered = applyFilters(data, filters);
    setFilteredData(filtered);
  };

  const handleExport = (format: 'pdf' | 'csv') => {
    if (format === 'pdf') {
      exportToPDF(title, filteredData);
    } else {
      exportToCSV(title, filteredData);
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'health':
        return <ChartBarIcon className="h-6 w-6 text-blue-500" />;
      case 'metrics':
        return <ChartBarIcon className="h-6 w-6 text-green-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      case 'config':
        return <CogIcon className="h-6 w-6 text-gray-500" />;
      case 'security':
        return <ShieldCheckIcon className="h-6 w-6 text-purple-500" />;
      case 'status':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (!isExpanded) return null;

    return (
      <div className="mt-4">
        <ChartFilter
          onFilterChange={handleFilterChange}
          onExport={handleExport}
          integrations={integrations}
        />
        {type === 'health' && (
          <div className={`grid gap-4 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Uptime Trend</h4>
              <div className="h-64">
                <Line data={filteredData.uptime} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  }
                }} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Response Time</h4>
              <div className="h-64">
                <Line data={filteredData.responseTime} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  }
                }} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Error Rate</h4>
              <div className="h-64">
                <Line data={filteredData.errorRate} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  }
                }} />
              </div>
            </div>
          </div>
        )}
        {type === 'metrics' && (
          <div className={`grid gap-4 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Request Volume</h4>
              <div className="h-64">
                <Bar data={filteredData.requestVolume} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  }
                }} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Success Rate</h4>
              <div className="h-64">
                <Pie data={filteredData.successRate} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false
                }} />
              </div>
            </div>
          </div>
        )}
        {type === 'error' && (
          <div className={`grid gap-4 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Recent Errors</h4>
              <div className="space-y-2">
                {filteredData.recentErrors.map((error: any, index: number) => (
                  <div key={index} className="p-2 bg-red-50 rounded">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-red-800">{error.error}</span>
                      <span className="text-xs text-red-600">{error.severity}</span>
                    </div>
                    <p className="text-xs text-red-600">{error.context}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Error Distribution</h4>
              <div className="h-64">
                <Pie data={filteredData.errorDistribution} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false
                }} />
              </div>
            </div>
          </div>
        )}
        {type === 'config' && (
          <div className={`grid gap-4 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Version History</h4>
              <div className="space-y-2">
                {filteredData.versions.map((version: any, index: number) => (
                  <div key={index} className="p-2 bg-indigo-50 rounded">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-indigo-800">v{version.version}</span>
                      <span className="text-xs text-indigo-600">{version.date.toLocaleDateString()}</span>
                    </div>
                    <p className="text-xs text-indigo-600">{version.changes}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Rate Limits</h4>
              <div className="h-64">
                <Bar data={filteredData.rateLimits} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  }
                }} />
              </div>
            </div>
          </div>
        )}
        {type === 'security' && (
          <div className={`grid gap-4 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Access Logs</h4>
              <div className="space-y-2">
                {filteredData.accessLogs.map((log: any, index: number) => (
                  <div key={index} className="p-2 bg-green-50 rounded">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-green-800">{log.action}</span>
                      <span className="text-xs text-green-600">{log.status}</span>
                    </div>
                    <p className="text-xs text-green-600">{log.user} - {log.timestamp.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Security Metrics</h4>
              <div className="h-64">
                <Pie data={filteredData.securityMetrics} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false
                }} />
              </div>
            </div>
          </div>
        )}
        {type === 'status' && (
          <div className={`grid gap-4 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Status History</h4>
              <div className="space-y-2">
                {filteredData.statusHistory.map((status: any, index: number) => (
                  <div key={index} className="p-2 bg-blue-50 rounded">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-blue-800">{status.status}</span>
                      <span className="text-xs text-blue-600">{status.duration}</span>
                    </div>
                    <p className="text-xs text-blue-600">{status.timestamp.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Status Distribution</h4>
              <div className="h-64">
                <Pie data={filteredData.statusDistribution} options={{ 
                  responsive: true, 
                  maintainAspectRatio: false
                }} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 rounded-lg"
      >
        <div className="flex items-center space-x-3">
          {getIcon()}
          <span className="text-sm font-medium text-gray-900">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {renderContent()}
    </div>
  );
};

const IntegrationsAdmin: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: '1',
      name: 'OpenAI GPT-4',
      provider: 'openai',
      status: 'active',
      config: {
        version: '1.0.0',
        rateLimit: 100,
        timeout: 30000,
        retryAttempts: 3,
        model: 'gpt-4',
        temperature: 0.7,
        maxTokens: 2048
      },
      health: {
        status: 'healthy',
        lastChecked: new Date(),
        responseTime: 250,
        errorRate: 0.1,
        uptime: 99.9
      },
      metrics: {
        totalRequests: 1000,
        successRate: 95,
        averageResponseTime: 300,
        successfulRequests: 950,
        failedRequests: 50,
        lastUpdated: new Date()
      },
      errorLogs: [],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: '2',
      name: 'Google Cloud AI',
      provider: 'google',
      status: 'pending',
      config: {
        version: '1.0.0',
        rateLimit: 200,
        timeout: 60000,
        retryAttempts: 5,
        projectId: 'your-project-id',
        region: 'us-central1'
      },
      health: {
        status: 'healthy',
        lastChecked: new Date(),
        responseTime: 180,
        errorRate: 0.2,
        uptime: 99.8
      },
      metrics: {
        totalRequests: 500,
        successRate: 90,
        averageResponseTime: 250,
        successfulRequests: 450,
        failedRequests: 50,
        lastUpdated: new Date()
      },
      errorLogs: [],
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [newIntegration, setNewIntegration] = useState<Partial<Integration>>({
    name: '',
    provider: 'openai',
    status: 'inactive',
    config: {
      version: '1.0.0',
      rateLimit: 100,
      timeout: 30000,
      retryAttempts: 3,
      apiKey: '',
      apiSecret: ''
    }
  });
  const [isCustomProvider, setIsCustomProvider] = useState(false);
  const [customProviderName, setCustomProviderName] = useState('');
  const [selectedTab, setSelectedTab] = useState<'overview' | 'health' | 'logs' | 'metrics'>('overview');
  const [isHealthCheckLoading, setIsHealthCheckLoading] = useState(false);
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');

  // Mock data for charts and insights
  const mockData = {
    health: {
      uptime: {
        labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
        datasets: [{
          label: 'Uptime %',
          data: [99.9, 99.8, 99.9, 99.7, 99.9, 99.8],
          borderColor: 'rgb(34, 197, 94)',
          tension: 0.1
        }]
      },
      responseTime: {
        labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
        datasets: [{
          label: 'Response Time (ms)',
          data: [250, 280, 220, 300, 260, 240],
          borderColor: 'rgb(59, 130, 246)',
          tension: 0.1
        }]
      },
      errorRate: {
        labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
        datasets: [{
          label: 'Error Rate %',
          data: [0.1, 0.2, 0.1, 0.3, 0.2, 0.1],
          borderColor: 'rgb(239, 68, 68)',
          tension: 0.1
        }]
      }
    },
    metrics: {
      requestVolume: {
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [{
          label: 'Total Requests',
          data: [1200, 1900, 1500, 2100, 1800, 1400, 1600],
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
        }]
      },
      successRate: {
        labels: ['Successful', 'Failed'],
        datasets: [{
          data: [95, 5],
          backgroundColor: ['rgb(34, 197, 94)', 'rgb(239, 68, 68)'],
        }]
      }
    },
    error: {
      recentErrors: [
        { timestamp: new Date(), error: 'Rate limit exceeded', severity: 'high', context: 'API Gateway' },
        { timestamp: new Date(), error: 'Connection timeout', severity: 'medium', context: 'Database' },
        { timestamp: new Date(), error: 'Invalid response format', severity: 'low', context: 'External API' },
      ],
      errorDistribution: {
        labels: ['High', 'Medium', 'Low'],
        datasets: [{
          data: [15, 45, 40],
          backgroundColor: ['rgb(239, 68, 68)', 'rgb(234, 179, 8)', 'rgb(34, 197, 94)'],
        }]
      }
    },
    config: {
      versions: [
        { version: '1.0.0', date: new Date(), changes: 'Initial release' },
        { version: '1.1.0', date: new Date(), changes: 'Added rate limiting' },
        { version: '1.2.0', date: new Date(), changes: 'Improved error handling' },
      ],
      rateLimits: {
        labels: ['OpenAI', 'Google', 'Custom'],
        datasets: [{
          label: 'Rate Limit (requests/min)',
          data: [100, 200, 150],
          backgroundColor: 'rgba(99, 102, 241, 0.5)',
        }]
      }
    },
    security: {
      accessLogs: [
        { timestamp: new Date(), user: 'admin', action: 'API key rotation', status: 'success' },
        { timestamp: new Date(), user: 'system', action: 'Secret rotation', status: 'success' },
        { timestamp: new Date(), user: 'admin', action: 'Access granted', status: 'success' },
      ],
      securityMetrics: {
        labels: ['Encrypted', 'Plain Text'],
        datasets: [{
          data: [85, 15],
          backgroundColor: ['rgb(34, 197, 94)', 'rgb(239, 68, 68)'],
        }]
      }
    },
    status: {
      statusHistory: [
        { timestamp: new Date(), status: 'active', duration: '2h 30m' },
        { timestamp: new Date(), status: 'maintenance', duration: '15m' },
        { timestamp: new Date(), status: 'active', duration: '5h 45m' },
      ],
      statusDistribution: {
        labels: ['Active', 'Inactive', 'Pending'],
        datasets: [{
          data: [60, 30, 10],
          backgroundColor: ['rgb(34, 197, 94)', 'rgb(239, 68, 68)', 'rgb(234, 179, 8)'],
        }]
      }
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    // TODO: Implement API call
    // For now, using mock data
    setIntegrations([
      {
        id: '1',
        name: 'OpenAI GPT-4',
        provider: 'openai',
        status: 'active',
        config: {
          version: '1.0.0',
          rateLimit: 100,
          timeout: 30000,
          retryAttempts: 3,
          model: 'gpt-4',
          temperature: 0.7,
          maxTokens: 2000,
        },
        health: {
          status: 'healthy',
          lastChecked: new Date(),
          responseTime: 500,
          errorRate: 0.1,
          uptime: 99.9
        },
        metrics: {
          totalRequests: 1000,
          successRate: 95,
          averageResponseTime: 500,
          successfulRequests: 950,
          failedRequests: 50,
          lastUpdated: new Date()
        },
        errorLogs: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: '2',
        name: 'Google Cloud AI',
        provider: 'google',
        status: 'pending',
        config: {
          version: '1.0.0',
          rateLimit: 100,
          timeout: 30000,
          retryAttempts: 3,
          projectId: 'your-project-id',
          region: 'us-central1',
        },
        health: {
          status: 'degraded',
          lastChecked: new Date(),
          responseTime: 700,
          errorRate: 0.2,
          uptime: 99.8
        },
        metrics: {
          totalRequests: 500,
          successRate: 90,
          averageResponseTime: 700,
          successfulRequests: 450,
          failedRequests: 50,
          lastUpdated: new Date()
        },
        errorLogs: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      }
    ]);
  };

  const handleAddIntegration = () => {
    if (newIntegration.name && newIntegration.provider) {
      const integration: Integration = {
        id: Date.now().toString(),
        name: newIntegration.name,
        provider: newIntegration.provider,
        status: newIntegration.status || 'inactive',
        config: {
          version: '1.0.0',
          rateLimit: 100,
          timeout: 30000,
          retryAttempts: 3,
          ...newIntegration.config
        },
        health: {
          status: 'healthy',
          lastChecked: new Date(),
          responseTime: 250,
          errorRate: 0.1,
          uptime: 99.9
        },
        metrics: {
          totalRequests: 0,
          successRate: 100,
          averageResponseTime: 0,
          successfulRequests: 0,
          failedRequests: 0,
          lastUpdated: new Date()
        },
        errorLogs: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setIntegrations([...integrations, integration]);
      setNewIntegration({
        name: '',
        provider: 'openai',
        status: 'inactive',
        config: {
          version: '1.0.0',
          rateLimit: 100,
          timeout: 30000,
          retryAttempts: 3,
          apiKey: '',
          apiSecret: ''
        }
      });
    }
  };

  const handleEditIntegration = async (integration: Integration) => {
    // TODO: Implement API call
    setIntegrations(integrations.map(i => i.id === integration.id ? integration : i));
    setIsEditModalOpen(false);
    setSelectedIntegration(null);
  };

  const handleDeleteIntegration = async (integrationId: string) => {
    // TODO: Implement API call
    setIntegrations(integrations.filter(i => i.id !== integrationId));
  };

  const handleDeleteClick = (integration: Integration) => {
    setSelectedIntegration(integration);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedIntegration) {
      await handleDeleteIntegration(selectedIntegration.id);
      setIsDeleteModalOpen(false);
      setSelectedIntegration(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedIntegration(null);
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'openai':
        return '🤖';
      case 'google':
        return '🌐';
      case 'tensorflow':
        return '🧠';
      case 'quickbooks':
        return '💰';
      case 'mailchimp':
        return '📧';
      default:
        return '🔌';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'custom') {
      setIsCustomProvider(true);
      setNewIntegration({ ...newIntegration, provider: '' });
    } else {
      setIsCustomProvider(false);
      setNewIntegration({ ...newIntegration, provider: value });
    }
  };

  const handleCustomProviderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomProviderName(value);
    setNewIntegration({ ...newIntegration, provider: value });
  };

  const validateIntegrationConfig = (config: IntegrationConfig): string[] => {
    const errors: string[] = [];
    
    if (!config.version) {
      errors.push('Version is required');
    }
    
    const rateLimit = config.rateLimit;
    if (typeof rateLimit === 'number' && rateLimit < 0) {
      errors.push('Rate limit must be a positive number');
    }
    
    const timeout = config.timeout;
    if (typeof timeout === 'number' && timeout < 0) {
      errors.push('Timeout must be a positive number');
    }
    
    const retryAttempts = config.retryAttempts;
    if (typeof retryAttempts === 'number' && retryAttempts < 0) {
      errors.push('Retry attempts must be a positive number');
    }

    return errors;
  };

  const checkIntegrationHealth = async (integration: Integration) => {
    setIsHealthCheckLoading(true);
    try {
      // TODO: Implement actual health check API call
      // This is a mock implementation
      const mockHealth: IntegrationHealth = {
        status: Math.random() > 0.3 ? 'healthy' : 'degraded',
        lastChecked: new Date(),
        responseTime: Math.random() * 1000,
        errorRate: Math.random() * 5,
        uptime: 99.9
      };

      const updatedIntegration = {
        ...integration,
        health: mockHealth
      };

      // Update the integration in the list
      setIntegrations(integrations.map(i => 
        i.id === integration.id ? updatedIntegration : i
      ));
    } catch (error) {
      console.error('Health check failed:', error);
    } finally {
      setIsHealthCheckLoading(false);
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800';
      case 'unhealthy':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderHealthMetrics = (integration: Integration) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Status</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(integration.health.status)}`}>
              {integration.health.status}
            </span>
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {integration.health.uptime}%
          </p>
          <p className="text-sm text-gray-500">Uptime</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Response Time</h3>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {integration.health.responseTime.toFixed(2)}ms
          </p>
          <p className="text-sm text-gray-500">Average</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Error Rate</h3>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {integration.health.errorRate.toFixed(2)}%
          </p>
          <p className="text-sm text-gray-500">Last 24 hours</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Last Checked</h3>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {new Date(integration.health.lastChecked).toLocaleTimeString()}
          </p>
          <p className="text-sm text-gray-500">Auto-refresh every 5 minutes</p>
        </div>
      </div>
    );
  };

  const renderErrorLogs = (integration: Integration) => {
    return (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">Error Logs</h3>
        </div>
        <div className="border-t border-gray-200">
          <ul className="divide-y divide-gray-200">
            {integration.errorLogs.map((log, index) => (
              <li key={index} className="px-4 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className={`h-5 w-5 ${
                      log.severity === 'high' ? 'text-red-500' :
                      log.severity === 'medium' ? 'text-yellow-500' :
                      'text-blue-500'
                    }`} />
                    <p className="ml-2 text-sm font-medium text-gray-900">
                      {log.error}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500">
                    {new Date(log.timestamp).toLocaleString()}
                  </p>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Context: {log.context}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderMetrics = (integration: Integration) => {
    return (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">Usage Metrics</h3>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Total Requests</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {integration.metrics.totalRequests}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Success Rate</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {((integration.metrics.successfulRequests / integration.metrics.totalRequests) * 100).toFixed(2)}%
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Average Response Time</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {integration.metrics.averageResponseTime.toFixed(2)}ms
              </dd>
            </div>
          </dl>
        </div>
      </div>
    );
  };

  return (
    <IntegrationsContext.Provider value={{ integrations }}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Integrations Administration</h1>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add New Integration
          </button>
        </div>

        {/* Key Features Section */}
        <div className={`grid gap-6 ${expandedFeature ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
          <FeatureCard
            title="Health Monitoring"
            type="health"
            data={mockData.health}
            isExpanded={expandedFeature === 'health'}
            onToggle={() => setExpandedFeature(expandedFeature === 'health' ? null : 'health')}
          />
          <FeatureCard
            title="Metrics Dashboard"
            type="metrics"
            data={mockData.metrics}
            isExpanded={expandedFeature === 'metrics'}
            onToggle={() => setExpandedFeature(expandedFeature === 'metrics' ? null : 'metrics')}
          />
          <FeatureCard
            title="Error Management"
            type="error"
            data={mockData.error}
            isExpanded={expandedFeature === 'error'}
            onToggle={() => setExpandedFeature(expandedFeature === 'error' ? null : 'error')}
          />
          <FeatureCard
            title="Configuration Management"
            type="config"
            data={mockData.config}
            isExpanded={expandedFeature === 'config'}
            onToggle={() => setExpandedFeature(expandedFeature === 'config' ? null : 'config')}
          />
          <FeatureCard
            title="Security"
            type="security"
            data={mockData.security}
            isExpanded={expandedFeature === 'security'}
            onToggle={() => setExpandedFeature(expandedFeature === 'security' ? null : 'security')}
          />
          <FeatureCard
            title="Status Management"
            type="status"
            data={mockData.status}
            isExpanded={expandedFeature === 'status'}
            onToggle={() => setExpandedFeature(expandedFeature === 'status' ? null : 'status')}
          />
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Integration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {integrations.map((integration) => (
                <tr key={integration.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{integration.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-2xl mr-2">{getProviderIcon(integration.provider)}</span>
                      <span className="text-sm text-gray-900 capitalize">{integration.provider}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(integration.status)}`}>
                      {integration.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {integration.updatedAt.toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedIntegration(integration);
                        setIsEditModalOpen(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(integration)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedIntegration && (
          <div className="mt-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setSelectedTab('overview')}
                  className={`${
                    selectedTab === 'overview'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setSelectedTab('health')}
                  className={`${
                    selectedTab === 'health'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Health
                </button>
                <button
                  onClick={() => setSelectedTab('logs')}
                  className={`${
                    selectedTab === 'logs'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Error Logs
                </button>
                <button
                  onClick={() => setSelectedTab('metrics')}
                  className={`${
                    selectedTab === 'metrics'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Metrics
                </button>
              </nav>
            </div>

            <div className="mt-6">
              {selectedTab === 'overview' && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Integration Overview</h3>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Provider</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedIntegration.provider}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Version</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedIntegration.config.version}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(selectedIntegration.health.status)}`}>
                          {selectedIntegration.health.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(selectedIntegration.createdAt).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {selectedTab === 'health' && renderHealthMetrics(selectedIntegration)}
              {selectedTab === 'logs' && renderErrorLogs(selectedIntegration)}
              {selectedTab === 'metrics' && renderMetrics(selectedIntegration)}
            </div>
          </div>
        )}

        {/* Add Integration Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-lg font-medium mb-4">Add New Integration</h2>
              <form onSubmit={(e) => { e.preventDefault(); handleAddIntegration(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={newIntegration.name}
                      onChange={(e) => setNewIntegration({ ...newIntegration, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Provider</label>
                    <select
                      value={isCustomProvider ? 'custom' : newIntegration.provider}
                      onChange={handleProviderChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="openai">OpenAI</option>
                      <option value="google">Google Cloud AI</option>
                      <option value="tensorflow">TensorFlow</option>
                      <option value="quickbooks">QuickBooks</option>
                      <option value="mailchimp">MailChimp</option>
                      <option value="custom">Add a Provider</option>
                    </select>
                  </div>
                  {isCustomProvider && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Custom Provider Name</label>
                      <input
                        type="text"
                        value={customProviderName}
                        onChange={handleCustomProviderChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="Enter provider name"
                        required
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">API Key</label>
                    <input
                      type="password"
                      value={newIntegration.config?.apiKey || ''}
                      onChange={(e) => setNewIntegration({
                        ...newIntegration,
                        config: {
                          ...newIntegration.config,
                          apiKey: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">API Secret</label>
                    <input
                      type="password"
                      value={newIntegration.config?.apiSecret || ''}
                      onChange={(e) => setNewIntegration({
                        ...newIntegration,
                        config: {
                          ...newIntegration.config,
                          apiSecret: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddModalOpen(false);
                      setIsCustomProvider(false);
                      setCustomProviderName('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Add Integration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Integration Modal */}
        {isEditModalOpen && selectedIntegration && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-lg font-medium mb-4">Edit Integration</h2>
              <form onSubmit={(e) => { e.preventDefault(); handleEditIntegration(selectedIntegration); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={selectedIntegration.name}
                      onChange={(e) => setSelectedIntegration({ ...selectedIntegration, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={selectedIntegration.status}
                      onChange={(e) => setSelectedIntegration({ ...selectedIntegration, status: e.target.value as Integration['status'] })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">API Key</label>
                    <input
                      type="password"
                      value={selectedIntegration.config.apiKey || ''}
                      onChange={(e) => setSelectedIntegration({
                        ...selectedIntegration,
                        config: {
                          ...selectedIntegration.config,
                          apiKey: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">API Secret</label>
                    <input
                      type="password"
                      value={selectedIntegration.config.apiSecret || ''}
                      onChange={(e) => setSelectedIntegration({
                        ...selectedIntegration,
                        config: {
                          ...selectedIntegration.config,
                          apiSecret: e.target.value
                        }
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedIntegration(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && selectedIntegration && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center justify-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Integration</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to delete the integration "{selectedIntegration.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </IntegrationsContext.Provider>
  );
};

export default IntegrationsAdmin; 